/**
 * @file Service for global grid variables.
 * @author Maarten Lensink
 */
import { OnDestroy, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { LocalStorageService } from '../core/local-storage.service';
import { LocalisationService } from '../core/localisation.service';
import { SubscriptionPool } from '../core/helpers/subscription-pool';
import { detailLevelsNamed } from './helpers/detail-levels';
import { SettingsService } from '../settings.service';
import * as i0 from "@angular/core";
import * as i1 from "../core/local-storage.service";
import * as i2 from "../core/localisation.service";
import * as i3 from "@angular/router";
import * as i4 from "../settings.service";
/**
 * The service provides information from the HorizonComponent to other components. Also information about visible time is provided.
 * @export
 * @class GridService
 * @implements {OnDestroy}
 */
var GridService = /** @class */ (function () {
    function GridService(localStorageService, localisationService, router, route, settingsService) {
        var _this = this;
        this.localStorageService = localStorageService;
        this.localisationService = localisationService;
        this.router = router;
        this.route = route;
        this.settingsService = settingsService;
        /**
         * The current horizon state.
         * @private
         * @type {HorizonState}
         * @memberOf GridService
         */
        this.currentState = GridService.fromParamsToState(this.localStorageService, this.localisationService.currentLanguage.locale, {});
        /**
         * Handle of time out so we can cancel the timeout when needed.
         * @private
         * @type {number}
         * @memberOf GridService
         */
        this.waitForIt = null;
        /**
         * Notifies when the state of the horizon is changed.
         */
        this.stateChanged = new EventEmitter();
        /**
         * Event when zoom changes
         */
        this.zoomChanged = new EventEmitter();
        /**
         * Available zoom levels and Current zoom index.
         * @see Also add these zoom levels to _grids.scss for generating the needed css rules.
         */
        this.availableZoom = [100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 750, 800, 850, 900, 950, 1000];
        // The index of the default zoom when the horizon is instantiated
        this.zoomIndex = 0;
        /** Keep track of the subscriptions */
        this.subscriptionPool = new SubscriptionPool();
        this.settingsOpen = false;
        this.subscriptionPool.add(this.localisationService.onLanguageChanged.subscribe(function (language) {
            _this.currentState.date.locale(language.code);
        }));
        // #4063: This implementation of debounceTime (when removing the 'waitForIt' construction) does not seem to work.
        // this.subscriptionPool.add(this.route.queryParams.pipe(debounceTime(750)).subscribe(params => {
        this.subscriptionPool.add(this.route.queryParams.subscribe(function (params) {
            var newState = GridService.fromParamsToState(_this.localStorageService, _this.localisationService.currentLanguage.locale, params);
            if (!_this.statesEqual(newState, _this.currentState)) {
                // Set the new state
                _this.currentState = newState;
                // Keep track of these variable over browser sessions
                _this.localStorageService.updateProperty('local', 'horizon-show-office-hours', newState.officeHours, [true, false]);
                _this.localStorageService.updateProperty('local', 'horizon-show-weekends', newState.weekends, [true, false]);
                _this.localStorageService.updateProperty('session', 'horizon-show-level', newState.level.name, ['quarter', 'month', 'week', 'day']);
                _this.localStorageService.updateProperty('session', 'horizon-show-date', newState.date.format('YYYYMMDD'));
                if (_this.waitForIt != null) {
                    // Skip, the state has changed already
                    window.clearTimeout(_this.waitForIt);
                }
                _this.waitForIt = window.setTimeout(function () {
                    _this.waitForIt = null;
                    // Fire event
                    _this.stateChanged.emit({});
                }, 750);
            }
        }));
    }
    Object.defineProperty(GridService.prototype, "date", {
        /**
         * Get the date
         */
        get: function () {
            return this.currentState.date;
        },
        /**
         * Set the date
         */
        set: function (date) {
            // Make sure we receive a Moment object (input element gives a Date for example)
            if (!moment.isMoment(date)) {
                date = moment(date);
            }
            if (!this.currentState.date.isSame(date, 'd')) {
                this.router.navigate([], { queryParamsHandling: 'merge', queryParams: this.fromStateToParams({ date: date }) });
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "level", {
        /**
         * Get the level
         */
        get: function () {
            return this.currentState.level;
        },
        /**
         * Set the date
         */
        set: function (level) {
            if (this.currentState.level.name !== level.name) {
                this.router.navigate([], { queryParamsHandling: 'merge', queryParams: this.fromStateToParams({ level: level }) });
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "officeHours", {
        /**
         * Get the office hours
         */
        get: function () {
            return this.currentState.officeHours;
        },
        /**
         * Set the office hours
         */
        set: function (officeHours) {
            if (this.currentState.officeHours !== officeHours) {
                this.router.navigate([], { queryParamsHandling: 'merge', queryParams: this.fromStateToParams({ officeHours: officeHours }) });
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "weekends", {
        /**
         * Get the weekends
         */
        get: function () {
            return this.currentState.weekends;
        },
        /**
         * Set the weekends
         */
        set: function (weekends) {
            if (this.currentState.weekends !== weekends) {
                this.router.navigate([], { queryParamsHandling: 'merge', queryParams: this.fromStateToParams({ weekends: weekends }) });
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "startDate", {
        /**
         * Readonly access to the end date, calculated based on start and level
         */
        get: function () {
            return this.level.startForDate(this.currentState.date);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "endDate", {
        /**
         * Readonly access to the end date, calculated based on start and level
         */
        get: function () {
            return this.level.endForDate(this.currentState.date);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "visibleHoursStart", {
        /**
        * The visible hours (start)
        *
        * @readonly
        * @type {number}
        */
        get: function () {
            if (this.officeHours) {
                return this.settingsService.settings.frontend.grid.officeHours.enabled.start;
            }
            else {
                return this.settingsService.settings.frontend.grid.officeHours.disabled.start;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "visibleHoursEnd", {
        /**
         * The visible hours (finish)
         *
         * @readonly
         * @type {number}
         */
        get: function () {
            if (this.officeHours) {
                return this.settingsService.settings.frontend.grid.officeHours.enabled.end;
            }
            else {
                return this.settingsService.settings.frontend.grid.officeHours.disabled.end;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "visibleDays", {
        /**
         * The visible days from the horizon
         *
         * @readonly
         * @type {Number[]}
         */
        get: function () {
            if (this.weekends) {
                return this.settingsService.settings.frontend.grid.weekends.enabled;
            }
            else {
                return this.settingsService.settings.frontend.grid.weekends.disabled;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridService.prototype, "zoom", {
        /** Get the zoom */
        get: function () {
            return this.availableZoom[this.zoomIndex];
        },
        /** Set the zoom */
        set: function (level) {
            var foundedZoomIndex = this.availableZoom.indexOf(level);
            if (this.zoomIndex === foundedZoomIndex) {
                return;
            }
            if (foundedZoomIndex !== -1) {
                this.zoomIndex = foundedZoomIndex;
            }
            else {
                console.warn('Unsupported zoom level (' + level + ')');
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Translates a set of params to a correct state
     * @param {Params} params
     * @returns {HorizonState}
     */
    GridService.fromParamsToState = function (localStorageService, locale, params) {
        // Create moment object if date (in string format) is given
        var paramDate = moment(params['date'], 'YYYYMMDD').locale(locale);
        // If the given date (via parameters) is valid use it and otherwise pick the value from the local storage (with a default)
        var date = (paramDate.isValid()) ?
            paramDate :
            moment(localStorageService.readProperty('session', 'horizon-show-date', moment().format('YYYYMMDD')), 'YYYYMMDD').locale(locale);
        // tslint:disable-next-line:max-line-length If the given level (via parameters) is valid use it and otherwise pick the value from the local storage (with a default)
        var level = (params['levelName'] !== undefined && detailLevelsNamed[params['levelName']]) ?
            detailLevelsNamed[params['levelName']] :
            detailLevelsNamed[localStorageService.readProperty('session', 'horizon-show-level', 'week', ['quarter', 'month', 'week', 'day'])];
        // If the given office hours (via parameters) is valid use it and otherwise pick the value from the local storage (with a default)
        var officeHours = (params['officeHours'] !== undefined) ?
            (params['officeHours'] === 'true') :
            localStorageService.readProperty('local', 'horizon-show-office-hours', true, [true, false]);
        // If the given weekends (via parameters) is valid use it and otherwise pick the value from the local storage (with a default)
        var weekends = (params['weekends'] !== undefined) ?
            (params['weekends'] === 'true') :
            localStorageService.readProperty('local', 'horizon-show-weekends', false, [true, false]);
        return {
            'date': date,
            'level': level,
            'officeHours': officeHours,
            'weekends': weekends
        };
    };
    /** Callback is typically used for any custom cleanup that needs to occur when the instance is destroyed. */
    GridService.prototype.ngOnDestroy = function () {
        this.subscriptionPool.unsubscribeAll();
    };
    /**
     * Set (a subset) of Horizon values at once
     * @param horizonClick The horizon params
     */
    GridService.prototype.setHorizon = function (newState) {
        if (!newState) {
            console.warn('No new horizon state is given');
            return;
        }
        if (this.currentState.level.name !== newState.level.name || !this.currentState.date.isSame(newState.date, 'd')) {
            this.router.navigate([], { queryParamsHandling: 'merge', queryParams: this.fromStateToParams(newState) });
        }
        else {
            console.warn('Given HorizonState is equal to current HorizonState');
        }
    };
    /**
     * Translates a exiting state to params (string based)
     * @param {Params} newState
     * @returns {Params}
     */
    GridService.prototype.fromStateToParams = function (newState) {
        return {
            'officeHours': (newState.officeHours !== undefined) ? newState.officeHours : this.currentState.officeHours,
            'weekends': (newState.weekends !== undefined) ? newState.weekends : this.currentState.weekends,
            'levelName': (newState.level !== undefined) ? newState.level.name : this.currentState.level.name,
            'date': (newState.date !== undefined) ? newState.date.format('YYYYMMDD') : this.currentState.date.format('YYYYMMDD')
        };
    };
    /**
     * Check if two states are equal
     * @param {HorizonState} stateFirst
     * @param {HorizonState} stateSecond
     * @returns
     */
    GridService.prototype.statesEqual = function (stateFirst, stateSecond) {
        var isEqual = true;
        // Test if office hours is equal
        if (stateFirst.officeHours !== stateSecond.officeHours) {
            isEqual = false;
        }
        // Test if weekends is equal
        if (stateFirst.weekends !== stateSecond.weekends) {
            isEqual = false;
        }
        // Test if level is equal
        if (stateFirst.level.name !== stateSecond.level.name) {
            isEqual = false;
        }
        // Test if date is equal
        if (!stateFirst.date.isSame(stateSecond.date, 'd')) {
            isEqual = false;
        }
        return isEqual;
    };
    /**
     * Gets the EntityFormat that is chosen by the filtername
     * @param filter filtername
     * @memberOf GridService
     */
    GridService.prototype.getFormat = function (filter) {
        switch (filter) {
            case 'Team':
                return {
                    dataSource: 'TeamItems',
                    entity: 'Team',
                    fields: ['Name']
                };
            case 'Competence':
                return {
                    dataSource: 'FunctionItems',
                    entity: 'Function',
                    fields: ['Name']
                };
            case 'Region':
                return {
                    dataSource: 'Regions',
                    entity: 'Location',
                    fields: ['Name']
                };
            case 'Resource':
                return {
                    dataSource: 'ResourceItems',
                    entity: 'Resource',
                    fields: ['Name', 'FirstName', 'Prefix'],
                    displayFormat: '[Name], [FirstName] [Prefix]'
                };
            case 'Project':
                return {
                    dataSource: 'ProjectItems',
                    entity: 'Project',
                    parameters: {
                        start: this.startDate.toDate(),
                        finish: this.endDate.toDate()
                    },
                    fields: ['Name']
                };
        }
    };
    GridService.ngInjectableDef = i0.defineInjectable({ factory: function GridService_Factory() { return new GridService(i0.inject(i1.LocalStorageService), i0.inject(i2.LocalisationService), i0.inject(i3.Router), i0.inject(i3.ActivatedRoute), i0.inject(i4.SettingsService)); }, token: GridService, providedIn: "root" });
    return GridService;
}());
export { GridService };
